import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  Paper,
  Typography,
  IconButton,
  Drawer,
  Tooltip,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  NotificationsActive as BellIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getUserDepartmentComponents } from '../BaseURL';
import { BRAND, FONT_STACK } from '../../brand';

// The sidebar was a white card keyed to #1A9B7F — a teal that appears nowhere
// else in the product, so every department panel read as a different
// application from the Admin Panel sitting one click away. It is now the same
// navy rail with the same apex-green accent, from the shared brand tokens.
//
// Dark is also the right call for a permanent navigation surface: it stops
// competing with the content beside it. The old white sidebar had the same
// background as the cards in the working area, separated only by a hairline
// border, so the eye had to work out where the chrome ended and the data began.
const ACCENT = BRAND.green;

// Foreground ramp for text and icons sitting on the navy. Kept as named steps
// rather than sprinkling rgba() literals through the JSX, so the contrast
// relationships stay deliberate.
const ON_NAVY = {
  strong: '#FFFFFF',
  muted:  'rgba(255,255,255,.72)',
  faint:  'rgba(255,255,255,.46)',
  hover:  'rgba(255,255,255,.08)',
  line:   'rgba(255,255,255,.10)',
  tile:   'rgba(255,255,255,.10)',
};

const FULL_ACCESS_ROLES = ['Head Of Assembly', 'System Administrator'];

const DRAWER_WIDTH = 264;
const RAIL_WIDTH = 68;
const TRANSITION = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SIDEBAR_MODE_KEY = 'totalcare:departmentSidebarMode';

/**
 * Shared department panel layout — collapsible sidebar.
 *
 * Sidebar has two modes (persisted in localStorage):
 *   - 'fixed':    always full width (264px)
 *   - 'floating': collapses to a 68px icon rail; expands on hover
 *
 * Props:
 *   - departmentId    (number)  used by /getUserDepartmentComponents for role gating
 *   - departmentName  (string)  shown in the sidebar header
 *   - departmentIcon  (node)    icon for the header
 *   - menuItems       (array)   [{ key, label, icon, component }]
 */
export default function DepartmentLayout({
  departmentId,
  departmentName,
  departmentIcon,
  menuItems = [],
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [allowedKeys, setAllowedKeys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sidebar collapse state
  const [sidebarMode, setSidebarMode] = useState(
    () => localStorage.getItem(SIDEBAR_MODE_KEY) || 'fixed'
  );
  const [hovering, setHovering] = useState(false);
  const isFixed = sidebarMode === 'fixed';
  const isExpanded = isFixed || hovering;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleMode = () => {
    const next = isFixed ? 'floating' : 'fixed';
    setSidebarMode(next);
    localStorage.setItem(SIDEBAR_MODE_KEY, next);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const saved = localStorage.getItem('userInfo');
        if (!saved) throw new Error('No user info found');
        const user = JSON.parse(saved);

        // Skip permission API entirely if no departmentId provided (e.g. Pharmacy)
        // or if user has full access role
        if (departmentId == null || FULL_ACCESS_ROLES.includes(user.role)) {
          if (!cancelled) setAllowedKeys(null);
          return;
        }

        try {
          const res = await axios.post(getUserDepartmentComponents, {
            roleId: user.role,
            departmentId,
          });
          if (cancelled) return;
          if (res.data?.success && Array.isArray(res.data.components)) {
            const allowed = res.data.components
              .filter((c) => c.permissions && c.permissions.access === true)
              .map((c) => c.component_name);
            setAllowedKeys(allowed.length ? allowed : null);
          } else {
            setAllowedKeys(null);
          }
        } catch {
          setAllowedKeys(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load department');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [departmentId]);

  const visibleItems = useMemo(() => {
    if (!allowedKeys) return menuItems;
    return menuItems.filter((it) => allowedKeys.includes(it.key));
  }, [menuItems, allowedKeys]);

  useEffect(() => {
    if (activeTab >= visibleItems.length && visibleItems.length > 0) {
      setActiveTab(0);
    }
  }, [visibleItems.length, activeTab]);

  const activeItem = visibleItems[activeTab];
  const ActiveComponent = activeItem?.component;

  const handleSelect = (idx) => {
    setActiveTab(idx);
    if (isMobile) setMobileOpen(false);
  };

  // Cross-tab navigation API exposed to render functions
  const navigateTo = (key) => {
    const idx = visibleItems.findIndex((it) => it.key === key);
    if (idx >= 0) setActiveTab(idx);
  };
  const renderCtx = { navigate: navigateTo };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={42} sx={{ color: BRAND.navy }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 420 }}>{error}</Alert>
      </Box>
    );
  }

  // ── Sidebar content (used in both desktop floating panel and mobile drawer) ──
  const renderSidebar = ({ expanded, mobile = false }) => (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: `linear-gradient(175deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
      fontFamily: FONT_STACK,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        px: expanded ? 2.25 : 1,
        py: 2,
        borderBottom: `1px solid ${ON_NAVY.line}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minHeight: 64,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '11px',
          bgcolor: ON_NAVY.tile,
          border: `1px solid ${ON_NAVY.line}`,
          color: ACCENT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {departmentIcon}
        </Box>

        {/* Department name — fades out when collapsed */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          opacity: expanded ? 1 : 0,
          maxWidth: expanded ? 200 : 0,
          overflow: 'hidden',
          transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
        }}>
          <Typography sx={{
            fontSize: '0.6rem', fontWeight: 700,
            letterSpacing: '1.2px', textTransform: 'uppercase',
            color: ON_NAVY.faint, lineHeight: 1.2, mb: 0.25,
            whiteSpace: 'nowrap',
          }}>
            Department
          </Typography>
          <Typography sx={{
            fontWeight: 800, color: ON_NAVY.strong,
            fontSize: '0.94rem', lineHeight: 1.25, letterSpacing: '-.1px',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {departmentName}
          </Typography>
        </Box>

        {/* Pin / unpin toggle — desktop only */}
        {!mobile && expanded && (
          <Tooltip title={isFixed ? 'Unpin (collapses on mouse leave)' : 'Pin sidebar open'} arrow>
            <IconButton
              size="small"
              onClick={toggleMode}
              sx={{
                color: isFixed ? ACCENT : ON_NAVY.faint,
                bgcolor: isFixed ? `${ACCENT}26` : 'transparent',
                '&:hover': { bgcolor: `${ACCENT}33`, color: ACCENT },
                width: 30, height: 30,
                flexShrink: 0,
              }}
            >
              {isFixed
                ? <PinIcon sx={{ fontSize: 17 }} />
                : <PinOutlinedIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Menu */}
      <Box sx={{
        flexGrow: 1, py: 1.5, overflowY: 'auto',
        // The default scrollbar is a light-on-light chrome control and looks
        // like a scratch down a dark rail.
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255,255,255,.16)', borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,.28)' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,255,255,.16) transparent',
      }}>
        {expanded && (
          <Typography sx={{
            px: 2.75, mb: 0.75,
            fontSize: '0.6rem', fontWeight: 700,
            color: ON_NAVY.faint, letterSpacing: '1.1px', textTransform: 'uppercase',
            opacity: expanded ? 1 : 0,
            transition: `opacity 0.18s ${TRANSITION}`,
          }}>
            Navigation
          </Typography>
        )}

        <List sx={{ px: expanded ? 1.25 : 0.75, py: 0 }}>
          {visibleItems.length === 0 ? (
            expanded && (
              <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.85rem', color: ON_NAVY.faint, lineHeight: 1.6 }}>
                  No sections available for your role.
                </Typography>
              </Box>
            )
          ) : (
            visibleItems.map((item, idx) => {
              // Section heading: render once whenever the `section` value
              // changes vs the previous item. Hidden when the sidebar is
              // collapsed to a rail (no labels visible at all there); a
              // thin divider line takes its place so the groups still read.
              const prevSection = idx > 0 ? visibleItems[idx - 1].section : null;
              const showHeading = item.section && item.section !== prevSection;
              const isSelected = idx === activeTab;
              const badgeCount = Number(item.badge || 0);
              const hasBadge   = badgeCount > 0;

              // Inline pill (expanded sidebar): bell + count
              const expandedBadge = hasBadge && (
                <Box sx={{
                  ml: 0.75,
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 0.9, py: 0.25, borderRadius: 999,
                  // Solid red on navy. The old pale-pink pill was designed for a
                  // white sidebar and all but disappears here.
                  bgcolor: '#E5484D',
                  color: '#fff',
                  fontSize: '0.78rem', fontWeight: 800,
                  lineHeight: 1,
                  border: '1px solid rgba(255,255,255,.22)',
                  flexShrink: 0,
                  // Gentle pulse to draw the eye
                  animation: 'tcReqPulse 1.8s ease-in-out infinite',
                  '@keyframes tcReqPulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(220,38,38,0.35)' },
                    '50%':      { boxShadow: '0 0 0 4px rgba(220,38,38,0)' },
                  },
                }}>
                  <BellIcon sx={{ fontSize: 14 }} />
                  {badgeCount}
                </Box>
              );

              // Floating dot+count overlay (collapsed rail)
              const collapsedBadge = hasBadge && (
                <Box sx={{
                  position: 'absolute',
                  top: 3, right: 6,
                  minWidth: 18, height: 18,
                  px: 0.5, borderRadius: 999,
                  bgcolor: '#E5484D', color: '#fff',
                  fontSize: '0.72rem', fontWeight: 800,
                  lineHeight: 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  // Ring matches the rail, not white — a white halo on navy
                  // reads as a rendering artefact.
                  boxShadow: `0 0 0 2px ${BRAND.navyDark}`,
                  animation: 'tcReqPulseDark 1.8s ease-in-out infinite',
                  '@keyframes tcReqPulseDark': {
                    '0%, 100%': { boxShadow: `0 0 0 2px ${BRAND.navyDark}, 0 0 0 0 rgba(229,72,77,0.5)` },
                    '50%':      { boxShadow: `0 0 0 2px ${BRAND.navyDark}, 0 0 0 5px rgba(229,72,77,0)` },
                  },
                }}>
                  {badgeCount > 99 ? '99+' : badgeCount}
                </Box>
              );

              const button = (
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleSelect(idx)}
                  sx={{
                    position: 'relative',
                    borderRadius: '10px',
                    mb: 0.375,
                    py: expanded ? 1 : 1.25,
                    px: expanded ? 1.5 : 0,
                    minHeight: 42,
                    overflow: 'hidden',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    transition: `padding 0.2s ${TRANSITION}, background-color .15s ease`,
                    '&:hover': { bgcolor: ON_NAVY.hover },
                    '&.Mui-selected': {
                      bgcolor: `${ACCENT}24`,
                      '&:hover': { bgcolor: `${ACCENT}30` },
                      // A green bar down the leading edge. On a dark rail a
                      // tinted background alone is a weak signal — at a glance
                      // the selected row and a hovered row look alike.
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0, top: 6, bottom: 6,
                        width: 3, borderRadius: '0 3px 3px 0',
                        background: ACCENT,
                      },
                      '& .MuiListItemIcon-root': { color: ACCENT },
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: expanded ? 32 : 0,
                    color: hasBadge ? '#FF8A8A' : ON_NAVY.muted,
                    justifyContent: 'center',
                    transition: 'color .15s ease',
                  }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                  </ListItemIcon>
                  <Box sx={{
                    flex: 1, minWidth: 0,
                    display: 'flex', alignItems: 'center',
                    opacity: expanded ? 1 : 0,
                    maxWidth: expanded ? 180 : 0,
                    overflow: 'hidden',
                    transition: `opacity 0.18s ${TRANSITION}, max-width 0.28s ${TRANSITION}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.875rem',
                      fontWeight: isSelected ? 700 : hasBadge ? 600 : 500,
                      color: isSelected ? ON_NAVY.strong : ON_NAVY.muted,
                      whiteSpace: 'nowrap',
                      transition: 'color .15s ease',
                    }}>
                      {item.label}
                    </Typography>
                    {expandedBadge}
                  </Box>
                  {!expanded && !mobile && collapsedBadge}
                </ListItemButton>
              );

              const tooltipTitle = hasBadge
                ? `${item.label} — ${badgeCount} pending`
                : item.label;

              return (
                <Box key={item.key}>
                  {/* Section heading. Shown when the sidebar is expanded —
                      otherwise we drop in a thin separator line so the
                      grouping is still implied on the collapsed rail. */}
                  {showHeading && (
                    expanded ? (
                      <Typography sx={{
                        px: 1.5,
                        mt: idx === 0 ? 0 : 1.5,
                        mb: 0.5,
                        fontSize: '0.6rem',
                        fontWeight: 800,
                        color: ON_NAVY.faint,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        userSelect: 'none',
                      }}>
                        {item.section}
                      </Typography>
                    ) : (
                      idx > 0 && (
                        <Box sx={{
                          height: 1, mx: 1.25, my: 0.5,
                          bgcolor: ON_NAVY.line,
                        }} />
                      )
                    )
                  )}
                  {!expanded && !mobile ? (
                    <Tooltip title={tooltipTitle} placement="right" arrow>
                      {button}
                    </Tooltip>
                  ) : button}
                </Box>
              );
            })
          )}
        </List>
      </Box>
    </Box>
  );

  const sidebarW = isExpanded ? DRAWER_WIDTH : RAIL_WIDTH;

  return (
    <Box sx={{
      display: 'flex',
      gap: { xs: 1.5, md: 2.5 },
      alignItems: 'flex-start',
      minHeight: 'calc(100vh - 96px)',
      width: '100%',
    }}>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            border: 'none',
            backgroundColor: BRAND.navy,
          },
        }}
      >
        {renderSidebar({ expanded: true, mobile: true })}
      </Drawer>

      {/* Desktop sidebar — collapsible card */}
      <Paper
        elevation={0}
        onMouseEnter={() => { if (!isFixed) setHovering(true); }}
        onMouseLeave={() => { if (!isFixed) setHovering(false); }}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: sidebarW,
          flexShrink: 0,
          // The Paper's own white background would show through at the rounded
          // corners before the navy child paints over it.
          bgcolor: BRAND.navy,
          border: 'none',
          borderRadius: '14px',
          height: 'calc(100vh - 96px)',
          position: 'sticky',
          top: 80,
          overflow: 'hidden',
          transition: `width 0.28s ${TRANSITION}, box-shadow 0.2s ease`,
          boxShadow: !isFixed && hovering
            ? `0 16px 40px ${BRAND.navy}45`
            : `0 6px 22px ${BRAND.navy}24`,
          // keep the floating expanded panel above content
          zIndex: !isFixed && hovering ? 5 : 1,
        }}
      >
        {renderSidebar({ expanded: isExpanded, mobile: false })}
      </Paper>

      {/* Main content */}
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}>
        {/* Mobile top bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            display: { xs: 'block', md: 'none' },
            // Matches the drawer it opens, so the phone header and the sidebar
            // are visibly the same surface.
            background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            mb: 1.5,
            boxShadow: `0 6px 18px ${BRAND.navy}2E`,
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              aria-label="open department menu"
              sx={{ mr: 1.5, color: '#fff' }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px',
                textTransform: 'uppercase', color: ON_NAVY.faint, lineHeight: 1.2,
              }}>
                {departmentName}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }} noWrap>
                {activeItem?.label || departmentName}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ flexGrow: 1, width: '100%', minWidth: 0 }}>
          {activeItem?.render ? (
            activeItem.render(renderCtx)
          ) : ActiveComponent ? (
            <ActiveComponent />
          ) : (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography sx={{ color: '#64748b' }}>
                Select a section from the sidebar to begin.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
